import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { ChevronDownIcon } from '@icons/ChevronDownIcon';

import {
  isExpandableActive,
  isExpandableInitiallyExpanded,
  isLinkActive,
  resolveHref,
} from './active';
import {
  DISABLED_ITEM_CLASS,
  EXPANDABLE_BUTTON_BASE,
  NAV_ICON_CLASS,
  POPOVER_CLASS,
  POPOVER_ROW_BASE,
  SUB_ITEM_BASE,
} from './constants';
import { FloatingPopover } from './FloatingPopover';
import { ICON_MAP } from './iconMap';
import type { ExpandableChild, ExpandableItem } from './types';
import { useFloatingMenu } from './useFloatingMenu';

interface SidebarExpandableProps {
  item: ExpandableItem;
  collapsed: boolean;
  pathname: string;
}

function childHref(child: ExpandableChild): string {
  return resolveHref(child);
}

// A "coming soon" placeholder — rendered inert (a span in a div) in both the
// accordion and the popover, rather than as a clickable link.
function isDisabledSubLink(child: ExpandableChild): boolean {
  return child.disabled === true;
}

// An expandable nav group (Equipment, Availability — see navConfig). Expanded: a
// toggle button + accordion. Collapsed: a hover popover listing the children, and
// the only way to reach them, since the trigger's onClick is inert when collapsed.
export function SidebarExpandable({
  item,
  collapsed,
  pathname,
}: SidebarExpandableProps) {
  const active = isExpandableActive(item, pathname);
  const [expanded, setExpanded] = useState(() =>
    isExpandableInitiallyExpanded(item, pathname),
  );
  // The sidebar is turbo-permanent (never remounts), so re-derive expansion from
  // the URL on each navigation — matching the old Phlex sidebar, which computed
  // it server-side per page render. Manual toggles persist within a page (deps
  // unchanged) and reset on navigation.
  useEffect(() => {
    setExpanded(isExpandableInitiallyExpanded(item, pathname));
  }, [item, pathname]);
  const menu = useFloatingMenu({ enabled: collapsed });

  const Icon = ICON_MAP[item.icon];

  return (
    <div>
      <button
        type="button"
        ref={menu.setReference}
        className={cn(
          EXPANDABLE_BUTTON_BASE,
          active ? 'bg-dinnerplate-white-light' : 'hover:bg-dinnerplate-white-lighter',
        )}
        aria-expanded={collapsed ? undefined : expanded}
        onClick={collapsed ? undefined : () => setExpanded((prev) => !prev)}
        onMouseEnter={menu.show}
        onMouseLeave={menu.scheduleHide}
      >
        <div className="flex items-center gap-4">
          <Icon className={NAV_ICON_CLASS} aria-hidden="true" />
          <span className={cn('nav-text', collapsed && 'hidden')}>{item.label}</span>
        </div>
        <ChevronDownIcon
          className={cn(
            'size-5 shrink-0 text-icon-primary transform chevron-motion',
            collapsed && 'hidden',
            expanded && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {/* Accordion (expanded sidebar only). Figma nested-menu spec: a 1px
          rail dropping from the parent icon's centre (12px padding + half a
          15px icon ≈ ml-6), compact sub-rows to its right. */}
      <div className={cn('mt-2', (collapsed || !expanded) && 'hidden')}>
        <div className="ml-6 border-l border-disable pl-3">
          <ul className="space-y-3">
            {item.children.map((child) => (
              <li key={child.label}>
                {isDisabledSubLink(child) ? (
                  <div className={cn(SUB_ITEM_BASE, DISABLED_ITEM_CLASS)}>{child.label}</div>
                ) : (
                  <a
                    href={childHref(child)}
                    className={cn(
                      SUB_ITEM_BASE,
                      isLinkActive(child, pathname)
                        ? 'bg-dinnerplate-white-light'
                        : 'hover:bg-dinnerplate-white-lighter',
                    )}
                    data-turbo-frame="_top"
                  >
                    {child.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Floating popover (collapsed sidebar) */}
      <FloatingPopover menu={menu} className={POPOVER_CLASS}>
        <div className="flex flex-col gap-[3px]">
          <div className="px-[18px] py-3 bg-off-white rounded-lg">
            <div className="text-sm font-semibold text-text-secondary">{item.label}</div>
          </div>
          <div className="h-px bg-filled-2" />
          {/* Disabled placeholders stay inert here too (mirrors the accordion). */}
          {item.children.map((child) =>
            isDisabledSubLink(child) ? (
              <div key={child.label} className={cn(POPOVER_ROW_BASE, DISABLED_ITEM_CLASS)}>
                {child.label}
              </div>
            ) : (
              <a
                key={child.label}
                href={childHref(child)}
                className={cn(POPOVER_ROW_BASE, 'hover:bg-dinnerplate-white-lighter')}
                data-turbo-frame="_top"
              >
                {child.label}
              </a>
            ),
          )}
        </div>
      </FloatingPopover>
    </div>
  );
}
